import { useMemo } from "react";

// Simulated IOS-XE boot monitor shown while the real CML lab cold-boots.
// Purely cosmetic: rows never show "ready" until the backend says so.
const LOG = [
  [0.0, "System Bootstrap, Version 17.18(2r), RELEASE SOFTWARE (fc1)"],
  [0.4, "Copyright (c) 1994-2026 by cisco Systems, Inc."],
  [1.2, "Initializing memory ................ 2048 MB"],
  [2.0, "Loading image flash0:/iol-xe-17.18.02.bin ..."],
  [3.5, "Decompressing ################################################"],
  [7.0, "Cisco IOS XE Software, Version 17.18.02"],
  [8.5, "Restricted Rights Legend — Use, duplication, or disclosure by the Government is subject to restrictions."],
  [10.0, "Installed image archive"],
  [11.5, "Processor board ID 9GP4X0Q1K2J"],
  [13.0, "Ethernet interfaces initializing ..."],
  [15.5, "%LINK-3-UPDOWN: Interface Ethernet0/0, changed state to up"],
  [17.0, "%LINEPROTO-5-UPDOWN: Line protocol on Interface Ethernet0/0, changed state to up"],
  [19.0, "%SYS-5-RESTART: System restarted --"],
  [21.0, "%PNP-6-PNP_DISCOVERY_STARTED: PnP Discovery started"],
  [23.5, "Console: applying lab starter configuration ..."],
  [27.0, "Waiting for console prompt ..."],
];

const STAGES = [
  [0, "powering on"],
  [3, "loading image"],
  [8, "decompressing"],
  [14, "initializing interfaces"],
  [22, "applying starter config"],
  [30, "waiting for prompt"],
];

const stageFor = (t) => STAGES.reduce((acc, [at, label]) => (t >= at ? label : acc), STAGES[0][1]);

export const BootSequence = ({ devices = [], elapsed = 0, estSec = 25, done = false }) => {
  const lines = useMemo(() => {
    const scale = estSec / 30;
    return LOG.filter(([at]) => elapsed >= at * scale).map(([, l]) => l);
  }, [elapsed, estSec]);

  return (
    <div
      className="absolute inset-0 flex flex-col bg-black/85 backdrop-blur-[1px] font-term"
      data-testid="boot-sequence"
    >
      <div className="flex-1 min-h-0 overflow-hidden px-4 pt-3 flex flex-col justify-end">
        {lines.map((l, i) => (
          <div
            key={i}
            className={`text-[11px] leading-5 whitespace-nowrap overflow-hidden text-ellipsis boot-line ${l.startsWith("%") ? "text-zinc-400" : "text-zinc-600"}`}
            data-testid={`boot-log-line-${i}`}
          >
            {l}
          </div>
        ))}
        {!done && <div className="text-[11px] leading-5 text-[#00FF41]/80 boot-cursor">▌</div>}
      </div>

      <div className="border-t border-zinc-900 bg-black/80 px-4 py-2" data-testid="boot-device-rows">
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          {devices.map((d, i) => {
            const t = Math.max(0, elapsed - i * 2.5);
            const ready = done;
            return (
              <div key={d.id} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em]" data-testid={`boot-device-row-${d.id}`}>
                <span className={ready ? "text-[#00FF41]" : "text-[#F59E0B] boot-pulse"}>{ready ? "●" : "◌"}</span>
                <span className="text-zinc-300">{d.id}</span>
                <span className={ready ? "text-[#00FF41]" : "text-zinc-500"}>{ready ? "ready" : stageFor(t)}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[10px] uppercase tracking-[0.25em]">
          <span className="text-[#F59E0B]">
            {done ? "lab up — attaching console" : `booting ${devices.length > 1 ? `${devices.length} cisco ios nodes` : "cisco ios node"}`}
          </span>
          <span className="text-zinc-600">{elapsed}s</span>
        </div>
        <div className="mt-1 h-0.5 bg-zinc-900 overflow-hidden">
          <div
            className="h-full bg-[#00FF41] transition-all duration-500"
            style={{ width: `${done ? 100 : Math.min(96, Math.round((elapsed / estSec) * 100))}%` }}
            data-testid="boot-progress-bar"
          />
        </div>
      </div>
    </div>
  );
};
